import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatChipInputEvent,
  MatChipList,
  MatChipListChange,
} from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { map, Observable, startWith } from 'rxjs';
import { without, find, propEq, sortWith, prop, ascend } from 'ramda';
import { titleCase } from '../../../../utils/functions';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'pulse-input-search',
  host: { class: 'pulse-input-search' },
  templateUrl: './input-search.component.html',
  styleUrls: ['./input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchComponent),
      multi: true,
    },
  ],
})
export class InputSearchComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  separatorKeysCodes: number[] = [ENTER, COMMA];
  itemCtrl = new FormControl('');
  filteredItems: Observable<any[]>;
  items: any[] = [];

  @Input() allItems: any[] = [];

  @Input() label: string = 'State';
  @Input() placeholder: string = 'Select a state';
  @Input() disabled: boolean;

  @Output() chipListChange: EventEmitter<MatChipListChange> =
    new EventEmitter<MatChipListChange>();
  @Output() pulseDisabledState: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() removeItem: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('itemInput') fruitInput: ElementRef<HTMLInputElement>;
  @ViewChild('chipList') chipList: MatChipList;

  constructor(protected el: ElementRef) {
    this.updateFilterItems();
  }

  ngOnChanges(): void {
    this.updateFilterItems();
  }

  ngOnInit(): void { }

  updateFilterItems() {
    this.filteredItems = this.itemCtrl.valueChanges.pipe(
      startWith(null),
      map((item: any) => {
        return item ? this._filter(item) : this.allItems.slice();
      })
    );
  }

  add(event: MatChipInputEvent): void {
    const value = titleCase((event.value || '').trim());
    if (find(propEq('description', value))(this.allItems)) {
      this.items.push(find(propEq('description', value))(this.allItems));
      event.chipInput!.clear();
      this.itemCtrl.setValue([]);
    }
  }

  remove(item: string): void {
    const index = this.items.indexOf(item);

    if (index >= 0) {
      this.items.splice(index, 1);
      this.allItems.push(item);
      this.allItems = sortWith([ascend(prop('code'))], this.allItems);
      this.updateFilterItems();
      this.removeItem.emit({ item, allItems: this.allItems });
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.items.push(event.option.value);
    this.allItems = without([event.option.value], this.allItems);
    this.itemCtrl.updateValueAndValidity();
    this.fruitInput.nativeElement.value = '';
    this.onTouched();
    this.onChange(this.items);
  }

  private _filter(value: any): string[] {
    const filterValue = value.description || value;
    return this.allItems.filter((item: any) => {
      if (item.description) {
        return (
          item.description.toLowerCase().substring(0, filterValue.length) ===
          filterValue.toLowerCase()
        );
      } else {
        return null;
      }
    });
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  onSelect(event: any) {
    this.onTouched();
    this.onChange();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    if (obj) {
      this.itemCtrl.setValue(obj);
      this.items = obj;
    } else {
      this.items = [];
      this.itemCtrl.setValue('');
    }
  }

  @HostListener('pulseDisabledState', ['$event'])
  handleDisabledStateEvent(value: boolean) {
    if (this.disabled !== value) {
      this.disabled = value;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.nativeInput) {
      this.nativeInput.disabled = isDisabled;
    }

    this.pulseDisabledState.emit(isDisabled);
  }

  get nativeInput(): HTMLInputElement {
    return this.el.nativeElement.querySelector('input');
  }
}
